import { cn } from '@/lib/utils';
import { useId } from 'react';
import { dialAngle, dialValue, DIAL_SWEEP } from './appearance-controls.util';
import { useValueControl } from './use-value-control.hook';

const HALF = DIAL_SWEEP / 2;
const SIZE = 44;
const CENTRE = SIZE / 2;

function point(radius: number, angle: number): [number, number] {
  const radians = ((angle - 90) * Math.PI) / 180;
  return [
    CENTRE + radius * Math.cos(radians),
    CENTRE + radius * Math.sin(radians),
  ];
}

function arc(radius: number, from: number, to: number): string {
  if (to - from < 0.01) return '';
  const [x1, y1] = point(radius, from);
  const [x2, y2] = point(radius, to);
  const large = to - from > 180 ? 1 : 0;
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${radius} ${radius} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

/**
 * Grain, 0 to 100, as a round dial turned by dragging round its centre.
 * Disabled, it takes no input and leaves the tab order.
 */
export function GrainDial({
  value,
  disabled = false,
  onChange,
  onCommit,
  onCancel,
}: {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const labelId = useId();
  const control = useValueControl({
    value,
    onChange,
    onCommit,
    onCancel,
    valueAt: (event, rect) =>
      dialValue(
        event.clientX - (rect.left + rect.width / 2),
        event.clientY - (rect.top + rect.height / 2)
      ),
  });
  const angle = dialAngle(value);
  const [dotX, dotY] = point(7, angle);

  return (
    <div className='flex shrink-0 flex-col items-center gap-1.5'>
      <span
        id={labelId}
        className='text-[12px] font-medium text-muted-foreground'
      >
        Grain
      </span>
      <div
        role='slider'
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-valuetext={`${value}%`}
        aria-disabled={disabled || undefined}
        data-angle={angle}
        {...(disabled ? {} : control)}
        className={cn(
          'relative size-11 touch-none rounded-full select-none',
          disabled ? 'opacity-40' : 'cursor-grab',
          'ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden'
        )}
      >
        <svg aria-hidden viewBox={`0 0 ${SIZE} ${SIZE}`} className='size-full'>
          <path
            d={arc(20, -HALF, HALF)}
            fill='none'
            strokeWidth={2.5}
            strokeLinecap='round'
            className='stroke-muted-foreground/25'
          />
          <path
            d={arc(20, -HALF, angle)}
            fill='none'
            strokeWidth={2.5}
            strokeLinecap='round'
            className='stroke-foreground'
          />
          <circle
            cx={CENTRE}
            cy={CENTRE}
            r={13}
            className='fill-background stroke-border'
            strokeWidth={1}
          />
          <circle cx={dotX} cy={dotY} r={2} className='fill-foreground' />
        </svg>
      </div>
    </div>
  );
}
